import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactFlowInstance } from 'reactflow';
import { useGraph, useSimulationStore } from '../../../store/simulationStore';

type SearchResult = {
  id: string;
  name: string;
  description?: string;
  score: number;
};

const normalize = (s: string) => s.toLowerCase().trim();

// Lightweight fuzzy scoring: subsequence bonus + token containment + position boosts
export const fuzzyScore = (query: string, text: string): number => {
  const q = normalize(query);
  const t = normalize(text);
  if (!q) return 0;

  // Exact/startsWith boosts
  if (t === q) return 1000;
  let score = 0;
  if (t.startsWith(q)) score += 300;
  if (t.includes(q)) score += 150;

  // Token containment
  const qTokens = q.split(/\s+/).filter(Boolean);
  const tTokens = t.split(/[^a-z0-9_]+/).filter(Boolean);
  let tokenHits = 0;
  for (const qt of qTokens) {
    if (t.includes(qt)) tokenHits += 1;
  }
  score += tokenHits * 60;

  // Subsequence match bonus
  let qi = 0;
  for (let i = 0; i < t.length && qi < q.length; i++) {
    if (t[i] === q[qi]) qi++;
  }
  if (qi === q.length) score += 120 + Math.max(0, 30 - (t.length - q.length));

  // Early position bonus
  const pos = t.indexOf(qTokens[0] ?? '');
  if (pos >= 0) score += Math.max(0, 60 - pos);

  // Length normalization (slight preference for shorter matches)
  score += Math.max(0, 40 - Math.max(0, t.length - q.length));

  return score;
};

interface NodeSearchProps {
  reactFlowInstance: ReactFlowInstance | null;
}

export const NodeSearch: React.FC<NodeSearchProps> = ({ 
  reactFlowInstance
}) => {
  const graph = useGraph();
  const setSelection = useSimulationStore((s) => s.setSelection);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const results: SearchResult[] = useMemo(() => {
    if (!query) return [];
    const q = query.trim();
    const scored = graph.nodes.map((n) => {
      const nameScore = fuzzyScore(q, n.data.name);
      const descScore = n.data.description ? fuzzyScore(q, n.data.description) : 0;
      // Prioritize name matches heavily
      const score = nameScore * 2 + descScore;
      return {
        id: n.id,
        name: n.data.name,
        description: n.data.description,
        score
      } as SearchResult;
    });
    return scored
      .filter((r) => r.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 8);
  }, [graph.nodes, query]);

  useEffect(() => {
    if (query) {
      setOpen(true);
      setActiveIndex(0);
    } else {
      setOpen(false);
    }
  }, [query]);

  const handleSelect = (res: SearchResult) => {
    setSelection(res.id);
    const rf = reactFlowInstance;
    // Prefer fitView to the node if supported; fallback to setCenter
    try {
      if (rf && typeof (rf as any).fitView === 'function') {
        (rf as any).fitView?.({ nodes: [{ id: res.id }], padding: 0.2, maxZoom: 1.5, duration: 500 });
      } else if (rf && typeof rf.setCenter === 'function') {
        const node = graph.nodes.find((n) => n.id === res.id);
        if (node) {
          rf.setCenter(node.position.x + 160, node.position.y + 40, { zoom: 1.2, duration: 500 });
        }
      }
    } catch {
      // ignore
    }
    setOpen(false);
    setQuery('');
    inputRef.current?.blur();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(results.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      handleSelect(results[activeIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
      inputRef.current?.blur();
    }
  };

  return (
    <div className="node-search">
      <div className="node-search-inner">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => query && setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Search nodes by name or notes…"
          className="node-search-input"
        />
        {open && results.length > 0 && (
          <div role="listbox" className="node-search-dropdown">
            {results.map((r, idx) => (
              <button
                key={r.id}
                role="option"
                aria-selected={idx === activeIndex}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(r)}
                onMouseEnter={() => setActiveIndex(idx)}
                className={`node-search-item ${idx === activeIndex ? 'active' : ''}`}
              >
                <div className="node-search-item-title">{r.name}</div>
                {r.description && (
                  <div className="node-search-item-description">
                    {r.description.length > 120 ? r.description.slice(0, 117) + '…' : r.description}
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NodeSearch;

export type { NodeSearchProps };
